import { Unsubscribe, Timestamp } from 'firebase/firestore';
import { BaseFirestoreService } from './base';
import type { KnowledgeBaseEntity, ExtractedData } from '../../types';

// Backend knowledge item structure (from /knowledge collection)
interface BackendKnowledgeItem {
  id: string;
  name: string;
  description: string;
  category: string;
  subcategory?: string;
  content: {
    extractedData?: ExtractedData & {
      bookingLinks?: Array<{ url: string; label: string; type: string; priority: number }>;
    };
    url?: string;
    images?: string[];
    confidence?: number;
    quality?: number;
  };
  searchKeywords?: string[];
  propertyId: string;
  createdAt: Timestamp | Date;
  updatedAt: Timestamp | Date;
}

/**
 * Service for managing knowledge base entities
 * Uses Firestore subcollections: /properties/{propertyId}/knowledge
 * Maps backend structure to admin entity structure
 */
export class KnowledgeBaseService extends BaseFirestoreService<BackendKnowledgeItem> {
  private propertyId: string;

  constructor(propertyId: string) {
    super(`properties/${propertyId}/knowledge`);
    this.propertyId = propertyId;
  }

  /**
   * Transform backend knowledge item to admin entity structure
   */
  private transformToEntity(item: BackendKnowledgeItem): KnowledgeBaseEntity {
    return {
      id: item.id,
      propertyId: item.propertyId,
      category: item.category as KnowledgeBaseEntity['category'],
      title: item.name,
      description: item.description || '',
      content: item.description || '',
      extractedData: item.content?.extractedData || {},
      images: item.content?.images || [],
      links: (item.content?.extractedData?.bookingLinks || []).map(link => ({
        url: link.url,
        text: link.label || 'Book Now',
        type: link.type === 'external' ? 'external' : 'booking',
        priority: link.priority || 0,
      })),
      source: 'scraper',
      sourceUrl: item.content?.url,
      createdAt: item.createdAt,
      updatedAt: item.updatedAt,
      confidence: item.content?.confidence || 0,
      completeness: item.content?.quality || 0,
      aiQualityScore: item.content?.quality || 0,
      keywords: item.searchKeywords || [],
      searchableText: `${item.name} ${item.description}`.toLowerCase(),
      tags: [item.category, item.subcategory].filter(Boolean) as string[],
      viewCount: 0,
      clickCount: 0,
      conversionCount: 0,
    };
  }

  /**
   * Subscribe to all entities for the property
   */
  subscribeToEntities(
    onUpdate: (entities: KnowledgeBaseEntity[]) => void,
    onError?: (error: Error) => void,
    category?: KnowledgeBaseEntity['category']
  ): Unsubscribe {
    return this.subscribeToCollection(
      (backendItems) => {
        // Skip special documents like _stats, _search_index, categories
        const filteredItems = backendItems.filter(
          item => !item.id.startsWith('_') && item.id !== 'categories' && item.name,
        );

        // Transform to admin entities
        let entities = filteredItems.map(item => this.transformToEntity(item));

        // Filter by category if specified
        if (category) {
          entities = entities.filter(e => e.category === category);
        }

        // Sort by most recently updated
        entities.sort((a, b) => {
          const aTime = a.updatedAt instanceof Date ? a.updatedAt.getTime() : (a.updatedAt as Timestamp)?.toMillis?.() ?? 0;
          const bTime = b.updatedAt instanceof Date ? b.updatedAt.getTime() : (b.updatedAt as Timestamp)?.toMillis?.() ?? 0;
          return bTime - aTime;
        });

        onUpdate(entities);
      },
      onError,
    );
  }

  /**
   * Subscribe to a specific entity
   */
  subscribeToEntity(
    entityId: string,
    onUpdate: (entity: KnowledgeBaseEntity | null) => void,
    onError?: (error: Error) => void
  ): Unsubscribe {
    return this.subscribeToDocument(
      entityId,
      (backendItem) => {
        if (backendItem && !backendItem.id.startsWith('_')) {
          onUpdate(this.transformToEntity(backendItem));
        } else {
          onUpdate(null);
        }
      },
      onError
    );
  }

  /**
   * Get all entities (one-time fetch)
   */
  async getAllEntities(category?: KnowledgeBaseEntity['category']): Promise<KnowledgeBaseEntity[]> {
    const constraints = [] as never[];
    const backendItems = await this.query(constraints);
    
    // Filter out special documents and transform
    const filteredItems = backendItems.filter(item => !item.id.startsWith('_'));
    let entities = filteredItems.map(item => this.transformToEntity(item));
    
    // Filter by category if specified
    if (category) {
      entities = entities.filter(e => e.category === category);
    }
    
    return entities;
  }

  /**
   * Get entities by source
   */
  async getEntitiesBySource(
    source: KnowledgeBaseEntity['source']
  ): Promise<KnowledgeBaseEntity[]> {
    // Backend doesn't have 'source' field, all items are from scraper
    const constraints = [] as never[];
    const backendItems = await this.query(constraints);
    
    const filteredItems = backendItems.filter(item => !item.id.startsWith('_'));
    let entities = filteredItems.map(item => this.transformToEntity(item));
    
    // Filter by source
    entities = entities.filter(e => e.source === source);
    
    return entities;
  }

  /**
   * Get recently updated entities
   */
  async getRecentlyUpdated(_limitCount: number = 10): Promise<KnowledgeBaseEntity[]> {
    const constraints = [] as never[];
    const backendItems = await this.query(constraints);
    
    const filteredItems = backendItems.filter(item => !item.id.startsWith('_'));
    const entities = filteredItems.map(item => this.transformToEntity(item));
    
    return entities;
  }
}

/**
 * Factory function to create knowledge base service for a property
 */
export function createKnowledgeBaseService(propertyId: string): KnowledgeBaseService {
  return new KnowledgeBaseService(propertyId);
}
